// src/api/mappers/riskMapper.js — VIRAJ SYSTEM v9.2
//
// Канон: ARBITRAGE_MODE_SPEC v9.2 — "все данные проходят через riskMapper.js"
// Назначение: нормализует данные из /api/risk/status и /api/risk/history
// для использования в ArbitragePage, Dashboard, CrisisHighlight, RecoveryHighlight.

/**
 * Нормализует один элемент risk_cache.
 * Приводит roi из доли к % для отображения.
 *
 * @param {object} row — строка из risk_cache
 * @returns {{
 *   date: string,
 *   risk_status: string,
 *   risk_score: number,
 *   roi_pct: number,        — % (уже умножен × 100 если доля)
 *   roi_raw: number,        — оригинальное значение
 *   spend: number,
 *   revenue: number,
 *   variance: number,
 *   acceleration: number,
 *   isCrisis: boolean,
 *   isRecovered: boolean,
 * }}
 */
export function mapRiskRow(row) {
  if (!row) return null;

  const risk_score  = Number(row.risk_score)  || 0;
  const risk_status = String(row.risk_status  || "unknown").toLowerCase();
  const roi_raw     = Number(row.roi)         || 0;
  // Канон HERO_LAYER: roi из БД — доля (0.41 = 41%)
  // Нормализация: Math.abs < 10 → доля → *100
  const roi_pct = Math.abs(roi_raw) < 10 ? roi_raw * 100 : roi_raw;

  return {
    date:         row.date ?? "",
    risk_status,
    risk_score,
    roi_pct:      Math.round(roi_pct * 10) / 10,  // 1 знак после запятой
    roi_raw,
    spend:        Number(row.spend)        || 0,
    revenue:      Number(row.revenue)      || 0,
    variance:     Number(row.variance)     || 0,
    acceleration: Number(row.acceleration) || 0,
    isCrisis:     risk_status === "critical" || risk_status === "red",
    isRecovered:  risk_status === "low"      || risk_status === "green",
  };
}

/**
 * Нормализует массив risk_history.
 *
 * @param {object[]} items
 * @returns {object[]}
 */
export function mapRiskHistory(items) {
  if (!Array.isArray(items)) return [];
  return items.map(mapRiskRow).filter(Boolean);
}

/**
 * Находит последний кризис из истории.
 *
 * @param {object[]} history — результат mapRiskHistory()
 * @returns {object|null}
 */
export function getLastCrisis(history) {
  return [...history]
    .filter(r => r.isCrisis)
    .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null;
}

/**
 * Находит первое восстановление после последнего кризиса.
 *
 * @param {object[]} history — результат mapRiskHistory()
 * @returns {object|null}
 */
export function getFirstRecovery(history) {
  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const lastCrisisIdx = sorted.map((r, i) => r.isCrisis ? i : -1).filter(i => i !== -1).pop() ?? -1;
  if (lastCrisisIdx === -1) return null;
  return sorted.slice(lastCrisisIdx + 1).find(r => r.isRecovered) ?? null;
}
